import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Package,
  Search,
  AlertTriangle,
  TrendingDown,
  TrendingUp,
  ArrowUpDown,
  Plus,
  Minus,
  ClipboardCheck,
  History,
  Loader2,
  Check,
  AlertCircle,
  X,
  PackageOpen,
  RotateCcw,
  Trash2,
  ArrowRightLeft,
  ChevronDown,
  ChevronRight,
  Edit2,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
  ContextMenuSeparator,
} from '@/components/ui/context-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useInventory, getEffectiveFOH, type InventoryItem, type InventoryTransaction } from '@/hooks/useInventory';
import { useProducts } from '@/hooks/useProducts';
import { useInventoryItems, type RawInventoryItem, type RawInventoryStock } from '@/hooks/useInventoryItems';
import { useCurrency } from '@/hooks/useCurrency';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Layers, Info } from 'lucide-react';
import { BOHTransferDialog } from '@/components/inventory/BOHTransferDialog';
import { ExpandedProductRow } from '@/components/products/ExpandedProductRow';
import { usePermissions } from '@/hooks/usePermissions';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ProductImportDialog, POSImportDialog } from '@/components/import';

// Composite product virtual inventory type
interface CompositeVirtualInventory {
  product_id: string;
  product_name: string;
  location_id: string;
  variant_id: string | null;
  variant_name: string | null;
  virtual_quantity: number;
  limiting_component: string | null;
  total_component_cost: number;
}

// Unified row type for the combined inventory table
type UnifiedInventoryRow =
  | { type: 'product'; item: InventoryItem; id: string }
  | { type: 'raw_material'; item: RawInventoryItem; stock: RawInventoryStock | undefined; id: string };

const transactionTypeColors: Record<string, string> = {
  received: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  received_boh: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  sold: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  adjusted: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  transferred: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  transfer_boh_to_foh: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  transfer_foh_to_boh: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
  returned: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  waste: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  consumed: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
};

// The ExpandedInventoryContent component has been moved to
// @/components/products/ExpandedProductRow.tsx as a shared component.
// It is now used in both ProductsList and InventoryOverview pages.

interface Toast {
  type: 'success' | 'error';
  message: string;
}

type AdjustmentType = 'received' | 'received_boh' | 'adjusted' | 'transferred' | 'returned' | 'waste';

type AdjustmentTarget = 'foh' | 'boh';

const adjustmentTypeLabels: Record<AdjustmentType, { label: string; icon: typeof TrendingUp; color: string }> = {
  received: { label: 'Receive (FOH)', icon: TrendingUp, color: 'text-green-600' },
  received_boh: { label: 'Receive (BOH)', icon: PackageOpen, color: 'text-blue-600' },
  adjusted: { label: 'Adjustment', icon: ArrowUpDown, color: 'text-blue-600' },
  transferred: { label: 'Transfer', icon: Package, color: 'text-purple-600' },
  returned: { label: 'Return', icon: RotateCcw, color: 'text-orange-600' },
  waste: { label: 'Waste/Loss', icon: Trash2, color: 'text-red-600' },
};

export default function InventoryOverview() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const {
    inventoryItems,
    isLoading,
    locationId,
    viewingAllLocations,
    fetchTransactions,
    createInventoryItem,
    adjustInventory,
    performStockCount,
    transferInventory,
  } = useInventory({ allowAllLocations: true });

  const { products } = useProducts();
  const { items: rawItems, stock: rawStock, createItem: createRawItem, adjustInventory: adjustRawInventory, unitOptions: rawUnitOptions, categoryOptions: rawCategoryOptions } = useInventoryItems({ allowAllLocations: true });
  const { formatCurrency } = useCurrency();
  const { hasPermission } = usePermissions();
  const canTransfer = hasPermission('inventory.transfers.create') || hasPermission('inventory.transfers.receive');
  const canCreateMenu = hasPermission('menu.create');
  const canImportMenu = hasPermission('menu.import_export');

  // Track expanded rows
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  // Cache associated products for expanded raw inventory items
  const [rawItemAssociated, setRawItemAssociated] = useState<Record<string, { parentId: string; parentName: string; parentImage: string | null; parentType: string | null; qtyPerUnit: number }[]>>({});
  const [rawItemAssociatedLoading, setRawItemAssociatedLoading] = useState<Set<string>>(new Set());
  const [focusedRowId, setFocusedRowId] = useState<string | null>(null);
  const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
  const handledDeepLinkProductId = useRef<string | null>(null);
  const focusTimerRef = useRef<number | null>(null);

  // Fetch composite product virtual inventory
  const { data: compositeInventory = [] } = useQuery({
    queryKey: ['composite-virtual-inventory', locationId],
    queryFn: async () => {
      if (!locationId || viewingAllLocations) return [];
      const { data, error } = await supabase
        .from('composite_product_virtual_inventory')
        .select('*')
        .eq('location_id', locationId);
      if (error) {
        console.error('Error fetching composite inventory:', error);
        return [];
      }
      return data as CompositeVirtualInventory[];
    },
    enabled: !!locationId && !viewingAllLocations,
  });

  // Build composite inventory lookup map (base-product level only, variant_id = null)
  const compositeInventoryMap = new Map(
    compositeInventory
      .filter(ci => ci.variant_id === null)
      .map(ci => [ci.product_id, ci])
  );

  // Create synthetic inventory items for composite products that lack direct inventory records
  const compositeProductItems: InventoryItem[] = useMemo(() => {
    if (viewingAllLocations || !locationId) return [];

    const existingProductIds = new Set(inventoryItems.map(i => i.product_id));

    return compositeInventory
      .filter(ci => ci.variant_id === null && !existingProductIds.has(ci.product_id))
      .map(ci => {
        const product = products.find(p => p.id === ci.product_id);
        return {
          id: `composite-virtual-${ci.product_id}`,
          product_id: ci.product_id,
          location_id: ci.location_id,
          quantity_on_hand: ci.virtual_quantity,
          quantity_reserved: 0,
          quantity_boh: 0,
          quantity_foh: ci.virtual_quantity,
          reorder_point: null,
          reorder_quantity: null,
          last_counted_at: null,
          created_at: product?.created_at || new Date().toISOString(),
          updated_at: product?.updated_at || new Date().toISOString(),
          product: product ? {
            id: product.id,
            name: product.name,
            sku: product.sku,
            barcode: product.barcode,
            price: product.price,
            cost: product.cost,
            image_url: product.image_url,
            is_active: product.is_active,
            is_available: product.is_available,
            is_sellable: product.is_sellable,
            category_id: product.category_id,
            product_type: product.product_type,
            tax_rate: product.tax_rate,
            description: product.description,
            category: product.category ? { id: product.category.id, name: product.category.name } : null,
          } : {
            id: ci.product_id,
            name: ci.product_name,
            sku: null,
            barcode: null,
            price: 0,
            cost: ci.total_component_cost || null,
            image_url: null,
            is_active: true,
            is_available: true,
            is_sellable: true,
            category_id: null,
            product_type: 'composite' as string,
            tax_rate: null,
            description: null,
          },
        } as InventoryItem;
      });
  }, [compositeInventory, inventoryItems, products, viewingAllLocations, locationId]);

  // Merge real inventory items with synthetic composite product items
  const allInventoryItems = useMemo(() => {
    return [...inventoryItems, ...compositeProductItems];
  }, [inventoryItems, compositeProductItems]);

  // Fetch raw material usage in composites (for "Available to Sell" column)
  const { data: rawMaterialUsage = [] } = useQuery({
    queryKey: ['raw-material-composite-usage'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('composite_product_components')
        .select(`
          raw_item_id,
          quantity,
          parent_product:products!parent_product_id(id, name)
        `)
        .not('raw_item_id', 'is', null);
      if (error) {
        console.error('Error fetching raw material usage:', error);
        return [];
      }
      return data as any[];
    },
  });

  // Fetch product-based component usage in composites (for product raw materials "Available to Sell")
  const { data: productComponentUsage = [] } = useQuery({
    queryKey: ['product-component-composite-usage'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('composite_product_components')
        .select(`
          component_product_id,
          quantity,
          parent_product:products!parent_product_id(id, name)
        `)
        .not('component_product_id', 'is', null);
      if (error) {
        console.error('Error fetching product component usage:', error);
        return [];
      }
      return data as any[];
    },
  });

  // Build a map: raw_item_id -> list of { composite_name, qty_per_composite }
  const rawMaterialCompositeMap = useMemo(() => {
    const map = new Map<string, { compositeName: string; qtyPerComposite: number }[]>();
    for (const usage of rawMaterialUsage) {
      if (!usage.raw_item_id) continue;
      const existing = map.get(usage.raw_item_id) || [];
      existing.push({
        compositeName: (usage.parent_product as any)?.name || 'Unknown',
        qtyPerComposite: usage.quantity || 0,
      });
      map.set(usage.raw_item_id, existing);
    }
    return map;
  }, [rawMaterialUsage]);

  // Build a map: component_product_id -> list of { composite_name, qty_per_composite }
  const productComponentCompositeMap = useMemo(() => {
    const map = new Map<string, { compositeName: string; qtyPerComposite: number }[]>();
    for (const usage of productComponentUsage) {
      if (!usage.component_product_id) continue;
      const existing = map.get(usage.component_product_id) || [];
      existing.push({
        compositeName: (usage.parent_product as any)?.name || 'Unknown',
        qtyPerComposite: usage.quantity || 0,
      });
      map.set(usage.component_product_id, existing);
    }
    return map;
  }, [productComponentUsage]);

  // Calculate "Available to Sell" for a raw material (raw_inventory_item)
  const getRawMaterialAvailableToSell = (rawItemId: string, currentStock: number): { total: number; details: { name: string; canMake: number }[] } => {
    const usages = rawMaterialCompositeMap.get(rawItemId);
    if (!usages || usages.length === 0) return { total: 0, details: [] };
    const details = usages.map(u => ({
      name: u.compositeName,
      canMake: u.qtyPerComposite > 0 ? Math.floor(currentStock / u.qtyPerComposite) : 0,
    }));
    const total = details.reduce((sum, d) => sum + d.canMake, 0);
    return { total, details };
  };

  // Calculate "Available to Sell" for a product-based component (is_sellable=false products used in composites)
  const getProductComponentAvailableToSell = (productId: string, currentStock: number): { total: number; details: { name: string; canMake: number }[] } => {
    const usages = productComponentCompositeMap.get(productId);
    if (!usages || usages.length === 0) return { total: 0, details: [] };
    const details = usages.map(u => ({
      name: u.compositeName,
      canMake: u.qtyPerComposite > 0 ? Math.floor(currentStock / u.qtyPerComposite) : 0,
    }));
    const total = details.reduce((sum, d) => sum + d.canMake, 0);
    return { total, details };
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'in_stock' | 'low' | 'out' | 'composite'>('all');
  const [itemTypeFilter, setItemTypeFilter] = useState<'all' | 'sellable' | 'raw_materials'>('all');
  const [activeFilter, setActiveFilter] = useState<'active' | 'inactive' | 'all'>('all');
  const [toast, setToast] = useState<Toast | null>(null);

  // Composite virtual inventory is location-specific; avoid showing a misleading view in "All Locations" mode.
  useEffect(() => {
    if (viewingAllLocations && filterStatus === 'composite') {
      setFilterStatus('all');
    }
  }, [viewingAllLocations, filterStatus]);

  // Realtime: subscribe to inventory changes so composite virtual inventory and raw materials refresh instantly
  useEffect(() => {
    if (!locationId || viewingAllLocations) return;

    // When inventory_items change (e.g. component stock consumed), refresh composite virtual inventory
    const channel = supabase
      .channel(`inv-overview-sync:${locationId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'inventory_items', filter: `location_id=eq.${locationId}` },
        () => {
          queryClient.invalidateQueries({ queryKey: ['composite-virtual-inventory'] });
          queryClient.invalidateQueries({ queryKey: ['raw-inventory-stock'] });
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'raw_inventory_stock' },
        () => {
          queryClient.invalidateQueries({ queryKey: ['raw-inventory-stock'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [locationId, viewingAllLocations, queryClient]);

  // Deep-link: /stock-control/overview?product=<product_id>
  // Uses allInventoryItems to support both real inventory items and synthetic composite items
  useEffect(() => {
    const productId = searchParams.get('product');
    if (!productId) {
      handledDeepLinkProductId.current = null;
      return;
    }

    // Prevent repeatedly forcing focus if user collapses after the initial jump.
    if (handledDeepLinkProductId.current === productId) return;

    // Ensure the target item isn't hidden by filters/search.
    setFilterStatus('all');
    setItemTypeFilter('all');
    setSearchQuery('');

    const targetItem = allInventoryItems.find((i) => i.product_id === productId);
    if (!targetItem) return;

    handledDeepLinkProductId.current = productId;

    // Expand and scroll into view.
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.add(targetItem.id);
      return next;
    });

    // Clear any existing focus timers
    if (focusTimerRef.current) {
      window.clearTimeout(focusTimerRef.current);
      focusTimerRef.current = null;
    }

    requestAnimationFrame(() => {
      const rowEl = rowRefs.current[targetItem.id];
      rowEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setFocusedRowId(targetItem.id);
      focusTimerRef.current = window.setTimeout(() => setFocusedRowId(null), 2500);
    });
  }, [searchParams, allInventoryItems]);

  // Adjustment dialog state
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [adjustmentType, setAdjustmentType] = useState<AdjustmentType>('received');
  const [adjustmentTarget, setAdjustmentTarget] = useState<AdjustmentTarget>('foh');
  const [adjustmentQuantity, setAdjustmentQuantity] = useState('');
  const [adjustmentNotes, setAdjustmentNotes] = useState('');
  const [adjusting, setAdjusting] = useState(false);

  // Stock count dialog state
  const [countDialogOpen, setCountDialogOpen] = useState(false);
  const [countItem, setCountItem] = useState<InventoryItem | null>(null);
  const [countQuantity, setCountQuantity] = useState('');
  const [countNotes, setCountNotes] = useState('');
  const [counting, setCounting] = useState(false);
  // BOH/FOH count mode
  const [countMode, setCountMode] = useState<'total' | 'split'>('total');
  const [countBOH, setCountBOH] = useState('');
  const [countFOH, setCountFOH] = useState('');

  // History dialog state
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyItem, setHistoryItem] = useState<InventoryItem | null>(null);
  const [transactions, setTransactions] = useState<InventoryTransaction[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Add inventory dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addDialogTab, setAddDialogTab] = useState<'track' | 'raw_material'>('track');
  const [selectedProductId, setSelectedProductId] = useState('');
  const [initialQuantity, setInitialQuantity] = useState('0');
  const [reorderPoint, setReorderPoint] = useState('');
  const [reorderQuantity, setReorderQuantity] = useState('');
  const [adding, setAdding] = useState(false);

  // Add raw material form state
  const [rawMaterialName, setRawMaterialName] = useState('');
  const [rawMaterialSku, setRawMaterialSku] = useState('');
  const [rawMaterialUnit, setRawMaterialUnit] = useState('each');
  const [rawMaterialCategory, setRawMaterialCategory] = useState('');
  const [rawMaterialCost, setRawMaterialCost] = useState('');
  const [rawMaterialInitialStock, setRawMaterialInitialStock] = useState('0');
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [posImportSource, setPosImportSource] = useState<string | null>(null);

  // BOH/FOH transfer dialog state
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferItem, setTransferItem] = useState<InventoryItem | null>(null);

  // Toggle row expansion
  const toggleRowExpansion = (itemId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  // Toggle expansion for raw inventory item rows and fetch associated composites
  const toggleRawItemExpansion = async (rawItemId: string) => {
    const key = `raw-${rawItemId}`;
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

    // Fetch associated products if not already cached
    if (!rawItemAssociated[rawItemId]) {
      setRawItemAssociatedLoading((prev) => new Set(prev).add(rawItemId));
      try {
        const { data, error } = await supabase
          .from('composite_product_components')
          .select(`
            quantity,
            parent_product:products!parent_product_id(id, name, image_url, product_type)
          `)
          .eq('raw_item_id', rawItemId);

        if (!error && data) {
          const results = data
            .filter((row: any) => row.parent_product)
            .map((row: any) => {
              const parent = Array.isArray(row.parent_product) ? row.parent_product[0] : row.parent_product;
              return {
                parentId: parent.id,
                parentName: parent.name,
                parentImage: parent.image_url,
                parentType: parent.product_type,
                qtyPerUnit: row.quantity,
              };
            });
          setRawItemAssociated((prev) => ({ ...prev, [rawItemId]: results }));
        }
      } catch (err) {
        console.error('Error fetching raw item associations:', err);
      } finally {
        setRawItemAssociatedLoading((prev) => {
          const next = new Set(prev);
          next.delete(rawItemId);
          return next;
        });
      }
    }
  };

  // Navigate to movements page filtered by product
  const handleViewMovements = (item: InventoryItem) => {
    navigate(`/stock-control/movements?product=${item.product_id}`);
  };

  // Handle edit product inside the unified stock control shell
  const handleEditProduct = (item: InventoryItem) => {
    navigate(`/stock-control/manage?edit=${item.product_id}`);
  };

  // Handle duplicate product
  const handleDuplicateProduct = (item: InventoryItem) => {
    navigate(`/stock-control/manage?duplicate=${item.product_id}`);
  };

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<UnifiedInventoryRow | null>(null);
  const [deleteNotes, setDeleteNotes] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [permanentDelete, setPermanentDelete] = useState(false);

  const openDeleteDialog = (row: UnifiedInventoryRow) => {
    setDeleteTarget(row);
    setDeleteNotes('');
    setPermanentDelete(false);
    setDeleteDialogOpen(true);
  };

  const getDeleteTargetStock = (): number => {
    if (!deleteTarget) return 0;
    if (deleteTarget.type === 'product') {
      const foh = getEffectiveFOH(deleteTarget.item);
      const boh = deleteTarget.item.quantity_boh ?? 0;
      return foh + boh;
    } else {
      return deleteTarget.stock?.quantity_on_hand ?? 0;
    }
  };

  const getDeleteTargetName = (): string => {
    if (!deleteTarget) return '';
    if (deleteTarget.type === 'product') return deleteTarget.item.product?.name || 'Unknown';
    return deleteTarget.item.name;
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const totalStock = getDeleteTargetStock();

      if (deleteTarget.type === 'product') {
        const item = deleteTarget.item;
        const isSynthetic = item.id.startsWith('composite-virtual-');

        // If stock exists and item has real inventory, zero it out as waste first
        if (totalStock > 0 && !isSynthetic) {
          const foh = getEffectiveFOH(item);
          const boh = item.quantity_boh ?? 0;

          if (foh > 0) {
            await adjustInventory.mutateAsync({
              inventory_item_id: item.id,
              quantity_change: -foh,
              transaction_type: 'waste',
              notes: deleteNotes || 'Zeroed out on deactivation (loss)',
            });
          }
          if (boh > 0) {
            const { error: bohError } = await supabase.rpc('adjust_boh_inventory', {
              p_inventory_item_id: item.id,
              p_quantity_change: -boh,
              p_notes: deleteNotes || 'Zeroed out BOH on deactivation (loss)',
            });
            if (bohError) console.error('Failed to zero BOH:', bohError);
          }
        }

        const { error } = await supabase
          .from('products')
          .update({ is_active: false, updated_at: new Date().toISOString() })
          .eq('id', item.product_id);
        if (error) throw error;

        queryClient.invalidateQueries({ queryKey: ['inventory'] });
        queryClient.invalidateQueries({ queryKey: ['products'] });
        queryClient.invalidateQueries({ queryKey: ['composite-virtual-inventory'] });
      } else {
        const rawItem = deleteTarget.item;
        const stock = deleteTarget.stock;

        if (totalStock > 0 && stock && locationId) {
          const { error: adjError } = await supabase.rpc('adjust_raw_inventory', {
            p_item_id: rawItem.id,
            p_location_id: locationId,
            p_quantity_change: -totalStock,
            p_transaction_type: 'waste',
            p_notes: deleteNotes || 'Zeroed out on deactivation (loss)',
          });
          if (adjError) console.error('Failed to zero raw stock:', adjError);
        }

        const { error } = await supabase
          .from('raw_inventory_items')
          .update({ is_active: false })
          .eq('id', rawItem.id);
        if (error) throw error;

        queryClient.invalidateQueries({ queryKey: ['raw-inventory-items'] });
        queryClient.invalidateQueries({ queryKey: ['raw-inventory-stock'] });
      }

      showToast('success', `${getDeleteTargetName()} deactivated`);
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    } catch (error: any) {
      showToast('error', error.message || 'Failed to deactivate item');
    } finally {
      setDeleting(false);
    }
  };

  // Handle permanent (hard) delete - removes the product from the database entirely
  const handlePermanentDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (deleteTarget.type === 'product') {
        const item = deleteTarget.item;
        const productName = item.product?.name || 'Unknown';
        const productSku = item.product?.sku || '';

        // Log the permanent deletion to audit_logs for traceability
        const { error: auditError } = await supabase.from('audit_logs').insert({
          action: 'permanent_delete',
          entity_type: 'product',
          entity_id: item.product_id,
          location_id: item.location_id,
          old_values: {
            name: productName,
            sku: productSku,
            price: item.product?.price,
            cost: item.product?.cost,
            quantity_on_hand: item.quantity_on_hand,
            quantity_foh: item.quantity_foh,
            quantity_boh: item.quantity_boh,
          },
          metadata: {
            notes: deleteNotes || 'Permanently deleted from inventory overview',
          },
        });
        if (auditError) console.error('Failed to write audit log:', auditError);

        // Clean up FK references that would block deletion:
        // 1. Nullify product_id on order_items (nullable, no CASCADE)
        await supabase
          .from('order_items')
          .update({ product_id: null })
          .eq('product_id', item.product_id);

        // 2. Delete purchase_order_items (has CHECK constraint requiring product_id OR raw_item_id)
        await supabase
          .from('purchase_order_items')
          .delete()
          .eq('product_id', item.product_id);

        // 3. Delete goods_request_items (NOT NULL, no CASCADE)
        await supabase
          .from('goods_request_items')
          .delete()
          .eq('product_id', item.product_id);

        // 4. Delete goods_transfer_items (NOT NULL, no CASCADE)
        await supabase
          .from('goods_transfer_items')
          .delete()
          .eq('product_id', item.product_id);

        // Hard delete the product (CASCADE handles inventory_items,
        // inventory_transactions, composite_product_components, etc.)
        const { error } = await supabase
          .from('products')
          .delete()
          .eq('id', item.product_id);
        if (error) throw error;

        queryClient.invalidateQueries({ queryKey: ['inventory'] });
        queryClient.invalidateQueries({ queryKey: ['products'] });
        queryClient.invalidateQueries({ queryKey: ['composite-virtual-inventory'] });
      } else {
        const rawItem = deleteTarget.item;

        // Hard delete the raw material (CASCADE handles stock records)
        const { error } = await supabase
          .from('raw_inventory_items')
          .delete()
          .eq('id', rawItem.id);
        if (error) throw error;

        queryClient.invalidateQueries({ queryKey: ['raw-inventory-items'] });
        queryClient.invalidateQueries({ queryKey: ['raw-inventory-stock'] });
      }

      showToast('success', `${getDeleteTargetName()} permanently deleted`);
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    } catch (error: any) {
      showToast('error', error.message || 'Failed to permanently delete item');
    } finally {
      setDeleting(false);
    }
  };

  // Handle print labels
  const handlePrintLabels = (item: InventoryItem) => {
    const product = item.product;
    if (!product) return;

    // Create a printable label window
    const printWindow = window.open('', '_blank', 'width=400,height=300');
    if (!printWindow) {
      showToast('error', 'Please allow popups to print labels');
      return;
    }

    const labelHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Product Label - ${product.name}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: Arial, sans-serif; 
            padding: 20px;
            display: flex;
            flex-direction: column;
            align-items: center;
          }
          .label {
            border: 2px solid #333;
            padding: 15px 20px;
            text-align: center;
            max-width: 300px;
          }
          .product-name {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 8px;
          }
          .sku {
            font-size: 12px;
            color: #666;
            margin-bottom: 10px;
          }
          .barcode {
            font-family: 'Libre Barcode 39', monospace;
            font-size: 48px;
            margin: 10px 0;
          }
          .barcode-text {
            font-size: 12px;
            margin-bottom: 8px;
          }
          .price {
            font-size: 24px;
            font-weight: bold;
          }
          @media print {
            body { padding: 0; }
            .no-print { display: none; }
          }
        </style>
        <link href="https://fonts.googleapis.com/css2?family=Libre+Barcode+39&display=swap" rel="stylesheet">
      </head>
      <body>
        <div class="label">
          <div class="product-name">${product.name}</div>
          ${product.sku ? `<div class="sku">SKU: ${product.sku}</div>` : ''}
          ${product.barcode ? `
            <div class="barcode">*${product.barcode}*</div>
            <div class="barcode-text">${product.barcode}</div>
          ` : ''}
          <div class="price">$${(product.price || 0).toFixed(2)}</div>
        </div>
        <button class="no-print" style="margin-top: 20px; padding: 10px 20px; cursor: pointer;" onclick="window.print()">
          Print Label
        </button>
      </body>
      </html>
    `;

    printWindow.document.write(labelHtml);
    printWindow.document.close();
  };

  // Auto-dismiss toast
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
  };

  // Filter inventory items by active/inactive status first
  const activeFilteredItems = useMemo(() => {
    return allInventoryItems.filter(item => {
      if (activeFilter === 'active') return item.product?.is_active !== false;
      if (activeFilter === 'inactive') return item.product?.is_active === false;
      return true;
    });
  }, [allInventoryItems, activeFilter]);

  // Filter raw materials by active/inactive status
  const activeFilteredRawItems = useMemo(() => {
    return rawItems.filter(item => {
      if (activeFilter === 'active') return item.is_active !== false;
      if (activeFilter === 'inactive') return item.is_active === false;
      return true;
    });
  }, [rawItems, activeFilter]);

  // Computed stats from active-filtered items (products + raw materials combined)
  const trackedCount = activeFilteredItems.length + activeFilteredRawItems.length;
  const inStockCount = activeFilteredItems.filter(i => {
    const fohQty = getEffectiveFOH(i);
    return fohQty > 0 && (!i.reorder_point || fohQty > i.reorder_point);
  }).length + activeFilteredRawItems.filter(item => {
    const stock = rawStock.find(s => s.inventory_item_id === item.id);
    const qty = stock?.quantity_on_hand ?? 0;
    return qty > 0 && (!stock?.reorder_point || qty > stock.reorder_point);
  }).length;
  const lowStockCount = activeFilteredItems.filter(
    item => item.reorder_point &&
      getEffectiveFOH(item) > 0 &&
      getEffectiveFOH(item) <= item.reorder_point
  ).length + activeFilteredRawItems.filter(item => {
    const stock = rawStock.find(s => s.inventory_item_id === item.id);
    return stock?.reorder_point ? (stock.quantity_on_hand ?? 0) <= stock.reorder_point && (stock.quantity_on_hand ?? 0) > 0 : false;
  }).length;
  const outOfStockCount = activeFilteredItems.filter(
    item => getEffectiveFOH(item) <= 0
  ).length + activeFilteredRawItems.filter(item => {
    const stock = rawStock.find(s => s.inventory_item_id === item.id);
    return (stock?.quantity_on_hand ?? 0) <= 0;
  }).length;

  // Get products that don't have inventory tracking enabled yet
  const productsWithoutInventory = products.filter(
    (p) => !allInventoryItems.some((inv) => inv.product_id === p.id)
  );

  // Toggle product active status from inventory row
  const [togglingActiveId, setTogglingActiveId] = useState<string | null>(null);
  const handleToggleActive = async (item: InventoryItem) => {
    if (!item.product?.id) return;
    const newActive = item.product.is_active === false;
    setTogglingActiveId(item.product.id);
    try {
      const { error } = await supabase
        .from('products')
        .update({ is_active: newActive, updated_at: new Date().toISOString() })
        .eq('id', item.product.id);
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['composite-virtual-inventory'] });
      showToast('success', `${item.product.name} ${newActive ? 'activated' : 'deactivated'}`);
    } catch (err: any) {
      showToast('error', err.message || 'Failed to update product status');
    } finally {
      setTogglingActiveId(null);
    }
  };

  // Toggle raw material active status
  const handleToggleRawActive = async (item: RawInventoryItem) => {
    const newActive = !item.is_active;
    setTogglingActiveId(item.id);
    try {
      const { error } = await supabase
        .from('raw_inventory_items')
        .update({ is_active: newActive })
        .eq('id', item.id);
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ['raw-inventory-items'] });
      showToast('success', `${item.name} ${newActive ? 'activated' : 'deactivated'}`);
    } catch (err: any) {
      showToast('error', err.message || 'Failed to update status');
    } finally {
      setTogglingActiveId(null);
    }
  };

  // Filter product inventory items
  const filteredItems = activeFilteredItems.filter((item) => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesName = item.product?.name.toLowerCase().includes(query);
      const matchesSku = item.product?.sku?.toLowerCase().includes(query);
      const matchesBarcode = item.product?.barcode?.toLowerCase().includes(query);
      if (!matchesName && !matchesSku && !matchesBarcode) return false;
    }

    // Item type filter
    if (itemTypeFilter === 'sellable') {
      if (item.product?.is_sellable === false) return false;
    } else if (itemTypeFilter === 'raw_materials') {
      if (item.product?.is_sellable !== false) return false;
    }

    // Status filter
    const fohQty = getEffectiveFOH(item);
    if (filterStatus === 'in_stock') {
      return fohQty > 0 && (!item.reorder_point || fohQty > item.reorder_point);
    }
    if (filterStatus === 'low') {
      return item.reorder_point && fohQty <= item.reorder_point && fohQty > 0;
    }
    if (filterStatus === 'out') {
      return fohQty <= 0;
    }

    return true;
  });

  // Filter raw materials
  const filteredRawItems = useMemo(() => {
    if (filterStatus === 'composite') return [];
    if (itemTypeFilter === 'sellable') return [];

    return activeFilteredRawItems.filter(item => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!item.name.toLowerCase().includes(q) && !item.sku?.toLowerCase().includes(q) && !item.barcode?.toLowerCase().includes(q)) return false;
      }

      if (filterStatus !== 'all') {
        const stockRecord = rawStock.find(s => s.inventory_item_id === item.id);
        const qty = stockRecord?.quantity_on_hand ?? 0;
        const reorderPt = stockRecord?.reorder_point;

        if (filterStatus === 'in_stock') {
          return qty > 0 && (!reorderPt || qty > reorderPt);
        }
        if (filterStatus === 'low') {
          return reorderPt ? qty <= reorderPt && qty > 0 : false;
        }
        if (filterStatus === 'out') {
          return qty <= 0;
        }
      }

      return true;
    });
  }, [activeFilteredRawItems, searchQuery, filterStatus, itemTypeFilter, rawStock]);

  // Build unified rows for the combined table
  const unifiedRows: UnifiedInventoryRow[] = useMemo(() => {
    const productRows: UnifiedInventoryRow[] = filteredItems.map(item => ({
      type: 'product' as const,
      item,
      id: `product-${item.id}`,
    }));
    const rawRows: UnifiedInventoryRow[] = filteredRawItems.map(item => ({
      type: 'raw_material' as const,
      item,
      stock: rawStock.find(s => s.inventory_item_id === item.id),
      id: `raw-${item.id}`,
    }));
    return [...productRows, ...rawRows];
  }, [filteredItems, filteredRawItems, rawStock]);

  // Open adjustment dialog
  const openAdjustDialog = (item: InventoryItem, type: AdjustmentType = 'received') => {
    setSelectedItem(item);
    setAdjustmentType(type);
    setAdjustmentTarget('foh');
    setAdjustmentQuantity('');
    setAdjustmentNotes('');
    setAdjustDialogOpen(true);
  };

  // Handle adjustment
  const handleAdjustment = async () => {
    if (!selectedItem || !adjustmentQuantity) return;

    const quantity = parseFloat(adjustmentQuantity);
    if (isNaN(quantity) || quantity === 0) {
      showToast('error', 'Please enter a valid quantity');
      return;
    }

    // For waste/sold, quantity should reduce stock (negative)
    const quantityChange = ['waste', 'sold'].includes(adjustmentType) ? -Math.abs(quantity) : quantity;

    // Determine the transaction type: if receiving to BOH, use received_boh
    let txnType: AdjustmentType = adjustmentType;
    if (adjustmentType === 'received' && adjustmentTarget === 'boh') {
      txnType = 'received_boh';
    }

    setAdjusting(true);
    try {
      await adjustInventory.mutateAsync({
        inventory_item_id: selectedItem.id,
        quantity_change: quantityChange,
        transaction_type: txnType,
        notes: adjustmentNotes || undefined,
      });
      const targetLabel = txnType === 'received_boh' ? ' (BOH)' : txnType === 'received' ? ' (FOH)' : '';
      showToast('success', `Stock adjusted successfully${targetLabel}`);
      setAdjustDialogOpen(false);
    } catch (error: any) {
      showToast('error', error.message || 'Failed to adjust stock');
    } finally {
      setAdjusting(false);
    }
  };

  // Open stock count dialog
  const openCountDialog = (item: InventoryItem) => {
    setCountItem(item);
    setCountQuantity(item.quantity_on_hand.toString());
    setCountNotes('');
    // Initialize BOH/FOH values
    const hasBOHFOH = (item.quantity_boh ?? 0) > 0 || (item.quantity_foh ?? 0) !== item.quantity_on_hand;
    setCountMode(hasBOHFOH ? 'split' : 'total');
    setCountBOH((item.quantity_boh ?? 0).toString());
    setCountFOH((item.quantity_foh ?? item.quantity_on_hand ?? 0).toString());
    setCountDialogOpen(true);
  };

  // Handle stock count
  const handleStockCount = async () => {
    if (!countItem) return;

    setCounting(true);
    try {
      if (countMode === 'split') {
        // Update BOH and FOH separately
        const bohQty = parseFloat(countBOH);
        const fohQty = parseFloat(countFOH);

        if (!Number.isFinite(bohQty) || !Number.isFinite(fohQty) || bohQty < 0 || fohQty < 0) {
          showToast('error', 'Enter valid, non-negative BOH and FOH quantities');
          setCounting(false);
          return;
        }

        const { error: countError } = await supabase.rpc('record_product_inventory_count', {
          p_inventory_item_id: countItem.id,
          p_quantity_boh: bohQty,
          p_quantity_foh: fohQty,
          p_notes: countNotes || `Stock count with BOH/FOH breakdown: BOH=${bohQty}, FOH=${fohQty}`,
        });

        if (countError) throw countError;

        // Invalidate inventory queries to refresh the UI
        queryClient.invalidateQueries({ queryKey: ['inventory'] });
        showToast('success', 'Stock count recorded with BOH/FOH breakdown');
      } else {
        // Original total-only count
        const quantity = parseFloat(countQuantity);
        if (isNaN(quantity) || quantity < 0) {
          showToast('error', 'Please enter a valid quantity');
          setCounting(false);
          return;
        }

        await performStockCount.mutateAsync({
          inventoryItemId: countItem.id,
          newQuantity: quantity,
          notes: countNotes || undefined,
        });
        showToast('success', 'Stock count recorded successfully');
      }
      setCountDialogOpen(false);
    } catch (error: any) {
      showToast('error', error.message || 'Failed to record stock count');
    } finally {
      setCounting(false);
    }
  };

  // Open history dialog
  const openHistoryDialog = async (item: InventoryItem) => {
    setHistoryItem(item);
    setHistoryDialogOpen(true);
    setLoadingHistory(true);
    try {
      const txns = await fetchTransactions(item.id);
      setTransactions(txns);
    } catch (error: any) {
      showToast('error', 'Failed to load transaction history');
    } finally {
      setLoadingHistory(false);
    }
  };

  // Handle add inventory (track existing product)
  const handleAddInventory = async () => {
    if (!selectedProductId || !locationId) return;

    setAdding(true);
    try {
      await createInventoryItem.mutateAsync({
        product_id: selectedProductId,
        location_id: locationId,
        quantity_on_hand: parseFloat(initialQuantity) || 0,
        reorder_point: reorderPoint ? parseFloat(reorderPoint) : null,
        reorder_quantity: reorderQuantity ? parseFloat(reorderQuantity) : null,
      });
      showToast('success', 'Inventory tracking enabled');
      setAddDialogOpen(false);
      setSelectedProductId('');
      setInitialQuantity('0');
      setReorderPoint('');
      setReorderQuantity('');
    } catch (error: any) {
      showToast('error', error.message || 'Failed to add inventory item');
    } finally {
      setAdding(false);
    }
  };

  // Handle add raw material
  const handleAddRawMaterial = async () => {
    if (!rawMaterialName.trim()) return;

    setAdding(true);
    try {
      const result = await createRawItem.mutateAsync({
        name: rawMaterialName.trim(),
        sku: rawMaterialSku.trim() || undefined,
        unit_of_measure: rawMaterialUnit,
        category: rawMaterialCategory || undefined,
        cost_per_unit: rawMaterialCost ? parseFloat(rawMaterialCost) : undefined,
      });

      // If initial stock > 0 and we have a location, adjust stock
      const initialStock = parseFloat(rawMaterialInitialStock) || 0;
      if (initialStock > 0 && locationId && result?.id) {
        await adjustRawInventory.mutateAsync({
          itemId: result.id,
          locationId,
          quantityChange: initialStock,
          transactionType: 'received',
          notes: 'Initial stock on creation',
        });
      }

      showToast('success', `${rawMaterialName} added`);
      setAddDialogOpen(false);
      setRawMaterialName('');
      setRawMaterialSku('');
      setRawMaterialUnit('each');
      setRawMaterialCategory('');
      setRawMaterialCost('');
      setRawMaterialInitialStock('0');
    } catch (error: any) {
      showToast('error', error.message || 'Failed to add raw material');
    } finally {
      setAdding(false);
    }
  };

  const getStockStatus = (item: InventoryItem) => {
    const isComposite = item.product?.product_type === 'composite' || item.product?.product_type === 'composite_variant';
    const fohQty = getEffectiveFOH(item);
    if (fohQty <= 0) {
      return { label: isComposite ? 'Out of Stock' : 'Out of Stock (FOH)', variant: 'destructive' as const };
    }
    if (item.reorder_point && fohQty <= item.reorder_point) {
      return { label: 'Low Stock', variant: 'warning' as const };
    }
    return { label: 'In Stock', variant: 'success' as const };
  };

  // Open transfer dialog
  const openTransferDialog = (item: InventoryItem) => {
    setTransferItem(item);
    setTransferDialogOpen(true);
  };

  // Handle BOH/FOH transfer
  const handleTransfer = async (data: {
    inventory_item_id: string;
    transfer_type: 'boh_to_foh' | 'foh_to_boh';
    quantity: number;
    notes?: string;
  }) => {
    try {
      await transferInventory.mutateAsync(data);
      showToast('success', `Transferred ${data.quantity} units ${data.transfer_type === 'boh_to_foh' ? 'to FOH' : 'to BOH'}`);
      setTransferDialogOpen(false);
    } catch (error: any) {
      showToast('error', error.message || 'Failed to transfer inventory');
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold">Inventory Overview</h2>
          <p className="text-sm text-muted-foreground">
            Track stock levels, adjust quantities, and manage reorder points
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Toast notification */}
          {toast && (
            <div
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium animate-in slide-in-from-top-2',
                toast.type === 'success' && 'bg-green-500/10 text-green-600 border border-green-500/20',
                toast.type === 'error' && 'bg-red-500/10 text-red-600 border border-red-500/20'
              )}
            >
              {toast.type === 'success' ? (
                <Check className="h-4 w-4" />
              ) : (
                <AlertCircle className="h-4 w-4" />
              )}
              {toast.message}
            </div>
          )}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant="blaze"
                    className="gap-2"
                    onClick={() => {
                      setAddDialogOpen(true);
                      setAddDialogTab('track');
                    }}
                    disabled={viewingAllLocations}
                  >
                    <Plus className="h-4 w-4" />
                    Track Item
                  </Button>
                </span>
              </TooltipTrigger>
              {viewingAllLocations && (
                <TooltipContent>
                  <p>Select a single outlet to track items.</p>
                  <p className="text-xs text-muted-foreground">
                    Tracking is location-specific. Switch from "All Locations" to a specific outlet.
                  </p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
          {canImportMenu && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2">
                  Import
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => setImportDialogOpen(true)}>
                  Import from BlazePOS Template
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Import from POS System</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => setPosImportSource('lightspeed')}>
                  Import from Lightspeed
                </DropdownMenuItem>
                <DropdownMenuItem disabled>
                  Import from Epay (Coming Soon)
                </DropdownMenuItem>
                <DropdownMenuItem disabled>
                  Import from DimePOS (Coming Soon)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {canCreateMenu && (
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => navigate('/stock-control/manage?create=true')}
            >
              <Plus className="h-4 w-4" />
              Add Product
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className={cn(
        "grid grid-cols-1 gap-4 mb-6",
        !viewingAllLocations && compositeInventory.length > 0 ? "md:grid-cols-5" : "md:grid-cols-4"
      )}>
        <Card
          className={cn(
            'cursor-pointer transition-colors',
            filterStatus === 'all' && 'ring-2 ring-primary'
          )}
          onClick={() => setFilterStatus('all')}
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-primary/10">
                <Package className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{trackedCount}</p>
                <p className="text-xs text-muted-foreground">Tracked Items</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className={cn(
            'cursor-pointer transition-colors',
            filterStatus === 'in_stock' && 'ring-2 ring-green-500'
          )}
          onClick={() => setFilterStatus(filterStatus === 'in_stock' ? 'all' : 'in_stock')}
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-green-500/10">
                <TrendingUp className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{inStockCount}</p>
                <p className="text-xs text-muted-foreground">In Stock (FOH)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className={cn(
            'cursor-pointer transition-colors',
            filterStatus === 'low' && 'ring-2 ring-yellow-500'
          )}
          onClick={() => setFilterStatus(filterStatus === 'low' ? 'all' : 'low')}
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-yellow-500/10">
                <AlertTriangle className="h-5 w-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{lowStockCount}</p>
                <p className="text-xs text-muted-foreground">Low Stock</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className={cn(
            'cursor-pointer transition-colors',
            filterStatus === 'out' && 'ring-2 ring-red-500'
          )}
          onClick={() => setFilterStatus(filterStatus === 'out' ? 'all' : 'out')}
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-red-500/10">
                <TrendingDown className="h-5 w-5 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{outOfStockCount}</p>
                <p className="text-xs text-muted-foreground">Out of Stock</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {!viewingAllLocations && compositeInventory.length > 0 && (
          <Card
            className={cn(
              'cursor-pointer transition-colors',
              filterStatus === 'composite' && 'ring-2 ring-purple-500'
            )}
            onClick={() => setFilterStatus(filterStatus === 'composite' ? 'all' : 'composite')}
          >
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-purple-500/10">
                  <Layers className="h-5 w-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-2xl font-semibold">{compositeInventory.length}</p>
                  <p className="text-xs text-muted-foreground">Composite Products</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, SKU, or barcode..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Item Type Filter */}
        <Select value={itemTypeFilter} onValueChange={(v) => setItemTypeFilter(v as 'all' | 'sellable' | 'raw_materials')}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All items" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Items</SelectItem>
            <SelectItem value="sellable">Sellable (POS)</SelectItem>
            <SelectItem value="raw_materials">Raw Materials</SelectItem>
          </SelectContent>
        </Select>

        {/* Active/Inactive Filter */}
        <Select value={activeFilter} onValueChange={(v) => setActiveFilter(v as 'active' | 'inactive' | 'all')}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Active" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>

        {(filterStatus !== 'all' || itemTypeFilter !== 'all' || activeFilter !== 'all') && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setFilterStatus('all');
              setItemTypeFilter('all');
              setActiveFilter('all');
            }}
            className="gap-2"
          >
            <X className="h-4 w-4" />
            Clear filters
          </Button>
        )}
      </div>

      {/* Inventory List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading inventory...</span>
          </div>
        </div>
      ) : !locationId ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <AlertCircle className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="font-medium">No location selected</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Please select a location to manage inventory
          </p>
        </div>
      ) : filterStatus === 'composite' ? (
        /* Composite Products Virtual Inventory View */
        <Card>
          <CardContent className="p-0">
            <div className="p-4 border-b bg-muted/30">
              <div className="flex items-center gap-2">
                <Layers className="h-5 w-5 text-purple-500" />
                <h3 className="font-medium">Composite Products</h3>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Virtual inventory based on component availability
              </p>
            </div>
            {compositeInventory.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Layers className="h-12 w-12 text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground">No composite products found</p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-4 font-medium text-muted-foreground">Product</th>
                    <th className="text-right p-4 font-medium text-muted-foreground">Can Make</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Limiting Component</th>
                    <th className="text-right p-4 font-medium text-muted-foreground">Component Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {compositeInventory.map((item) => (
                    <tr key={item.product_id} className="border-b last:border-0 hover:bg-muted/50">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-100/80 to-purple-50/80 dark:from-purple-950/50 dark:to-purple-900/30 flex items-center justify-center shrink-0">
                            <Layers className="h-4 w-4 text-purple-500/50" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium truncate">
                              {item.product_name}
                              {item.variant_name && <span className="text-muted-foreground"> - {item.variant_name}</span>}
                            </p>
                            <Badge variant="secondary" className="text-xs">
                              {item.variant_id ? 'Composite + Variant' : 'Composite'}
                            </Badge>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-right">
                        <span className={cn(
                          "font-semibold tabular-nums",
                          item.virtual_quantity <= 0 && "text-red-600",
                          item.virtual_quantity > 0 && item.virtual_quantity <= 5 && "text-yellow-600"
                        )}>
                          {item.virtual_quantity.toLocaleString()}
                        </span>
                      </td>
                      <td className="p-4">
                        {item.limiting_component ? (
                          <span className="text-sm text-muted-foreground">
                            {item.limiting_component}
                          </span>
                        ) : (
                          <span className="text-sm text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        <span className="text-sm font-medium">
                          {formatCurrency(item.total_component_cost)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      ) : unifiedRows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <PackageOpen className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="font-medium">No inventory items found</h3>
          <p className="text-sm text-muted-foreground mt-1 mb-4">
            {searchQuery || filterStatus !== 'all'
              ? 'Try adjusting your filters'
              : 'Start tracking inventory for your products'}
          </p>
          {!searchQuery && filterStatus === 'all' && (
            <Button
              variant="blaze"
              className="gap-2"
              onClick={() => setAddDialogOpen(true)}
            >
              <Plus className="h-4 w-4" />
              Track Item
            </Button>
          )}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 pl-4 font-medium text-muted-foreground w-[240px]">Item</th>
                  {viewingAllLocations && (
                    <th className="text-left p-3 font-medium text-muted-foreground w-[180px]">Outlet</th>
                  )}
                  <th className="text-left p-3 font-medium text-muted-foreground">Supplier</th>
                  <th className="text-right p-3 font-medium text-muted-foreground w-[80px]">
                    <span title="Front of House - Available for sale">FOH</span>
                  </th>
                  <th className="text-right p-3 font-medium text-muted-foreground w-[80px]">
                    <span title="Back of House - In storage">BOH</span>
                  </th>
                  <th className="text-right p-3 font-medium text-muted-foreground w-[80px]">Total</th>
                  {!viewingAllLocations && (
                    <th className="text-center p-3 font-medium text-muted-foreground w-[80px]">
                      <span title="Composite: units that can be sold based on component inventory">Can Sell</span>
                    </th>
                  )}
                  <th className="text-right p-3 font-medium text-muted-foreground w-[70px]">Margin</th>
                  <th className="text-left p-3 font-medium text-muted-foreground w-[120px]">Status</th>
                  <th className="text-center p-3 font-medium text-muted-foreground w-[60px]">Active</th>
                  <th className="text-left p-3 font-medium text-muted-foreground w-[100px]">Created</th>
                </tr>
              </thead>
              <tbody>
                {unifiedRows.map((row) => {
                  if (row.type === 'product') {
                    const item = row.item;
                    const status = getStockStatus(item);
                    const isExpanded = expandedRows.has(item.id);
                    const isRawMaterial = item.product?.is_sellable === false;
                    const isComposite = item.product?.product_type === 'composite' || item.product?.product_type === 'composite_variant';
                    const isSyntheticComposite = item.id.startsWith('composite-virtual-');
                    const fohQty = getEffectiveFOH(item);
                    const bohQty = item.quantity_boh ?? 0;
                    
                    return (
                      <ContextMenu key={row.id}>
                        <ContextMenuTrigger asChild>
                          <>
                            <tr
                              ref={(el) => { rowRefs.current[item.id] = el; }}
                              className={cn(
                                'border-b cursor-pointer transition-colors',
                                isExpanded ? 'bg-muted/30' : 'hover:bg-muted/50',
                                focusedRowId === item.id && 'bg-primary/10'
                              )}
                              onClick={() => toggleRowExpansion(item.id)}
                            >
                              <td className="p-3 pl-4">
                                <div className="flex items-center gap-2">
                                  <div className="shrink-0 w-5">
                                    {isExpanded ? (
                                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                    )}
                                  </div>
                                  <div className={cn(
                                    "w-9 h-9 rounded-lg flex items-center justify-center shrink-0 overflow-hidden",
                                    isComposite
                                      ? "bg-gradient-to-br from-purple-100/80 to-purple-50/80 dark:from-purple-950/50 dark:to-purple-900/30"
                                      : "bg-gradient-to-br from-blaze-100/80 to-blaze-50/80 dark:from-blaze-950/50 dark:to-blaze-900/30"
                                  )}>
                                    {item.product?.image_url ? (
                                      <img
                                        src={item.product.image_url}
                                        alt={item.product.name}
                                        className="w-full h-full object-cover"
                                      />
                                    ) : isComposite ? (
                                      <Layers className="h-4 w-4 text-purple-500/50" />
                                    ) : (
                                      <Package className="h-4 w-4 text-blaze-500/50" />
                                    )}
                                  </div>
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-1.5">
                                      <p className="font-medium truncate text-sm">{item.product?.name}</p>
                                      {isComposite && (
                                        <Badge variant="outline" className="text-[10px] px-1 py-0 bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/20 dark:text-purple-400 dark:border-purple-800 shrink-0">
                                          {item.product?.product_type === 'composite_variant' ? 'Composite + Variant' : 'Composite'}
                                        </Badge>
                                      )}
                                      {isRawMaterial && !isComposite && (
                                        <Badge variant="outline" className="text-[10px] px-1 py-0 bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800 shrink-0">
                                          Raw Material
                                        </Badge>
                                      )}
                                    </div>
                                    <p className="text-xs text-muted-foreground truncate">
                                      {item.product?.sku || 'No SKU'}
                                    </p>
                                  </div>
                                </div>
                              </td>
                              {viewingAllLocations && (
                                <td className="p-3">
                                  <span className="text-sm text-muted-foreground">
                                    {item.location?.name || '--'}
                                  </span>
                                </td>
                              )}
                              <td className="p-3">
                                <span className="text-sm text-muted-foreground">--</span>
                              </td>
                            <td className="p-3 text-right">
                              {isComposite ? (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className={cn(
                                        "font-semibold tabular-nums cursor-help",
                                        fohQty <= 0 && "text-red-600",
                                        fohQty > 0 && fohQty <= 5 && "text-yellow-600",
                                        fohQty > 5 && "text-green-600"
                                      )}>
                                        {fohQty.toLocaleString()}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">
                                      <p className="text-xs">Virtual stock from components</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : (
                                <span className={cn(
                                  "font-semibold tabular-nums",
                                  fohQty < 0 && "text-red-600 bg-red-100 dark:bg-red-900/30 px-1.5 py-0.5 rounded",
                                  fohQty === 0 && "text-red-600"
                                )}>
                                  {fohQty < 0 ? fohQty : fohQty.toLocaleString()}
                                </span>
                              )}
                            </td>
                            <td className="p-3 text-right">
                              {isComposite ? (
                                <span className="text-xs text-muted-foreground">--</span>
                              ) : (
                                <span className={cn(
                                  "tabular-nums",
                                  bohQty > 0 ? "text-blue-600 font-medium" : "text-muted-foreground"
                                )}>
                                  {bohQty.toLocaleString()}
                                </span>
                              )}
                            </td>
                            <td className="p-3 text-right">
                              {isComposite ? (
                                <span className="text-xs text-muted-foreground">--</span>
                              ) : (
                                <span className={cn(
                                  "text-sm tabular-nums",
                                  item.quantity_on_hand < 0 
                                    ? "text-red-600 font-medium" 
                                    : "text-muted-foreground"
                                )}>
                                  {item.quantity_on_hand < 0 ? item.quantity_on_hand : item.quantity_on_hand.toLocaleString()}
                                </span>
                              )}
                            </td>
                            {!viewingAllLocations && (
                              <td className="p-3 text-center">
                                {(() => {
                                  const ci = compositeInventoryMap.get(item.product_id);

                                  // For non-composite raw materials, show how many composites they can support
                                  if (!ci && isRawMaterial) {
                                    const foh = getEffectiveFOH(item);
                                    const avail = getProductComponentAvailableToSell(item.product_id, foh);
                                    if (avail.details.length === 0) return <span className="text-xs text-muted-foreground">--</span>;
                                    return (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <span className={cn(
                                              "inline-flex items-center gap-1 cursor-help font-semibold tabular-nums text-sm",
                                              avail.total <= 0 && "text-red-600",
                                              avail.total > 0 && avail.total <= 5 && "text-yellow-600",
                                              avail.total > 5 && "text-green-600"
                                            )}>
                                              {avail.total}
                                              <Info className="h-3 w-3 text-muted-foreground" />
                                            </span>
                                          </TooltipTrigger>
                                          <TooltipContent side="left" className="max-w-[260px]">
                                            <p className="font-medium mb-1">Composite products supported:</p>
                                            {avail.details.map((d, i) => (
                                              <p key={i} className="text-xs text-muted-foreground">
                                                {d.name}: {d.canMake} units
                                              </p>
                                            ))}
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    );
                                  }

                                  if (!ci) return <span className="text-xs text-muted-foreground">--</span>;
                                  return (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <span className="inline-flex items-center gap-1 cursor-help">
                                            <span className={cn(
                                              "font-semibold tabular-nums text-sm",
                                              ci.virtual_quantity <= 0 && "text-red-600",
                                              ci.virtual_quantity > 0 && ci.virtual_quantity <= 5 && "text-yellow-600",
                                              ci.virtual_quantity > 5 && "text-green-600"
                                            )}>
                                              {ci.virtual_quantity}
                                            </span>
                                            <Info className="h-3 w-3 text-muted-foreground" />
                                          </span>
                                        </TooltipTrigger>
                                        <TooltipContent side="left" className="max-w-[240px]">
                                          <p className="font-medium mb-1">
                                            Can make {ci.virtual_quantity} unit{ci.virtual_quantity !== 1 ? 's' : ''}
                                          </p>
                                          {ci.limiting_component && (
                                            <p className="text-xs text-muted-foreground">
                                              Limited by: {ci.limiting_component}
                                            </p>
                                          )}
                                          {ci.total_component_cost > 0 && (
                                            <p className="text-xs text-muted-foreground">
                                              Component cost: {formatCurrency(ci.total_component_cost)}
                                            </p>
                                          )}
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  );
                                })()}
                              </td>
                            )}
                            <td className="p-3 text-right">
                              {(() => {
                                const cost = item.product?.cost || 0;
                                const price = item.product?.price || 0;
                                if (price <= 0) {
                                  return <span className="text-xs text-muted-foreground">--</span>;
                                }
                                const marginPercent = ((price - cost) / price) * 100;
                                let colorClass = 'text-muted-foreground';
                                if (cost === 0 && price > 0) {
                                  colorClass = 'text-amber-600';
                                } else if (marginPercent < 0) {
                                  colorClass = 'text-red-600 font-medium';
                                } else if (marginPercent >= 30) {
                                  colorClass = 'text-green-600';
                                }
                                return (
                                  <span className={cn("text-xs tabular-nums", colorClass)}>
                                    {marginPercent.toFixed(0)}%
                                  </span>
                                );
                              })()}
                            </td>
                            <td className="p-3">
                              <Badge variant={status.variant}>{status.label}</Badge>
                            </td>
                            <td className="p-3 text-center">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleActive(item);
                                }}
                                disabled={togglingActiveId === item.product?.id}
                                className={cn(
                                  "transition-colors",
                                  togglingActiveId === item.product?.id
                                    ? "opacity-50 cursor-not-allowed"
                                    : "hover:opacity-80 cursor-pointer"
                                )}
                                title={item.product?.is_active !== false ? 'Deactivate product' : 'Activate product'}
                              >
                                {togglingActiveId === item.product?.id ? (
                                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                                ) : item.product?.is_active !== false ? (
                                  <ToggleRight className="h-5 w-5 text-green-600" />
                                ) : (
                                  <ToggleLeft className="h-5 w-5 text-muted-foreground" />
                                )}
                              </button>
                            </td>
                            <td className="p-3 text-left">
                              <span className="text-xs text-muted-foreground">
                                {new Date(item.created_at).toLocaleDateString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric',
                                  year: '2-digit'
                                })}
                              </span>
                            </td>
                          </tr>

                          {isExpanded && (
                            <ExpandedProductRow
                              inventoryItem={isSyntheticComposite ? undefined : item}
                              productId={item.product_id}
                              colSpan={viewingAllLocations ? 11 : 11}
                              actions={{
                                onEdit: () => handleEditProduct(item),
                                onDuplicate: () => handleDuplicateProduct(item),
                                onPrintLabels: () => handlePrintLabels(item),
                                onViewMovements: isSyntheticComposite ? undefined : () => handleViewMovements(item),
                                onAdjustInventory: isSyntheticComposite ? undefined : () => openAdjustDialog(item),
                                onStockCount: isSyntheticComposite ? undefined : () => openCountDialog(item),
                                onTransfer: isSyntheticComposite ? undefined : () => openTransferDialog(item),
                                onDelete: () => openDeleteDialog(row),
                                canTransfer: isSyntheticComposite ? false : canTransfer,
                              }}
                            />
                          )}
                        </>
                      </ContextMenuTrigger>
                      <ContextMenuContent>
                        <ContextMenuItem onClick={() => handleEditProduct(item)}>
                          <Edit2 className="h-4 w-4 mr-2" />
                          Edit Product
                        </ContextMenuItem>
                        {!isSyntheticComposite && (
                          <ContextMenuItem onClick={() => handleViewMovements(item)}>
                            <History className="h-4 w-4 mr-2 text-primary" />
                            View inventory movements
                          </ContextMenuItem>
                        )}
                        {!isSyntheticComposite && (
                          <>
                            <ContextMenuSeparator />
                            <ContextMenuItem onClick={() => openAdjustDialog(item, 'received')}>
                              <Plus className="h-4 w-4 mr-2 text-green-600" />
                              Add Stock
                            </ContextMenuItem>
                            <ContextMenuItem onClick={() => openAdjustDialog(item, 'waste')}>
                              <Minus className="h-4 w-4 mr-2 text-red-600" />
                              Remove Stock
                            </ContextMenuItem>
                            <ContextMenuSeparator />
                            {canTransfer && (
                              <ContextMenuItem onClick={() => openTransferDialog(item)}>
                                <ArrowRightLeft className="h-4 w-4 mr-2 text-purple-600" />
                                Transfer BOH / FOH
                              </ContextMenuItem>
                            )}
                            <ContextMenuItem onClick={() => openCountDialog(item)}>
                              <ClipboardCheck className="h-4 w-4 mr-2" />
                              Stock Count
                            </ContextMenuItem>
                            <ContextMenuItem onClick={() => openHistoryDialog(item)}>
                              <History className="h-4 w-4 mr-2" />
                              View History
                            </ContextMenuItem>
                          </>
                        )}
                        <ContextMenuSeparator />
                        <ContextMenuItem
                          onClick={() => openDeleteDialog(row)}
                          className="text-red-600 focus:text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete / Deactivate
                        </ContextMenuItem>
                      </ContextMenuContent>
                    </ContextMenu>
                  );
                  } else {
                    // Raw material row
                    const rawItem = row.item;
                    const stockRecord = row.stock;
                    const qty = stockRecord?.quantity_on_hand ?? 0;
                    const isLow = stockRecord?.reorder_point ? qty <= stockRecord.reorder_point && qty > 0 : false;
                    const isOut = qty <= 0;
                    const availToSell = getRawMaterialAvailableToSell(rawItem.id, qty);
                    const rawStatus = isOut
                      ? { label: 'Out of Stock', variant: 'destructive' as const }
                      : isLow
                        ? { label: 'Low Stock', variant: 'warning' as const }
                        : { label: 'In Stock', variant: 'success' as const };

                    const isRawExpanded = expandedRows.has(`raw-${rawItem.id}`);
                    const rawAssoc = rawItemAssociated[rawItem.id] || [];
                    const isRawAssocLoading = rawItemAssociatedLoading.has(rawItem.id);

                    return (
                      <React.Fragment key={row.id}>
                      <tr
                        className={cn(
                          "border-b last:border-0 cursor-pointer",
                          isRawExpanded ? 'bg-muted/30' : 'hover:bg-muted/50',
                        )}
                        onClick={() => toggleRawItemExpansion(rawItem.id)}
                      >
                        <td className="p-3 pl-4">
                          <div className="flex items-center gap-2">
                            <div className="shrink-0 w-5">
                              {isRawExpanded ? (
                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                              )}
                            </div>
                            <div className="w-9 h-9 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center shrink-0">
                              <Layers className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5">
                                <p className="font-medium text-sm truncate">{rawItem.name}</p>
                                <Badge variant="outline" className="text-[10px] px-1 py-0 bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800 shrink-0">
                                  Raw Material
                                </Badge>
                              </div>
                              <p className="text-xs text-muted-foreground truncate">
                                {rawItem.sku || rawItem.category?.replace('_', ' ') || 'No SKU'}
                              </p>
                            </div>
                          </div>
                        </td>
                        {viewingAllLocations && (
                          <td className="p-3">
                            <span className="text-sm text-muted-foreground">--</span>
                          </td>
                        )}
                        <td className="p-3">
                          <span className="text-sm text-muted-foreground">--</span>
                        </td>
                        <td className="p-3 text-right">
                          <span className={cn(
                            "font-semibold tabular-nums",
                            isOut && "text-red-600",
                            isLow && "text-yellow-600"
                          )}>
                            {qty.toLocaleString()}
                          </span>
                        </td>
                        <td className="p-3 text-right">
                          <span className="text-xs text-muted-foreground">--</span>
                        </td>
                        <td className="p-3 text-right">
                          <span className="text-xs text-muted-foreground">--</span>
                        </td>
                        {!viewingAllLocations && (
                          <td className="p-3 text-center">
                            {availToSell.details.length > 0 ? (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className={cn(
                                      "inline-flex items-center gap-1 cursor-help font-semibold tabular-nums text-sm",
                                      availToSell.total <= 0 && "text-red-600",
                                      availToSell.total > 0 && availToSell.total <= 5 && "text-yellow-600",
                                      availToSell.total > 5 && "text-green-600"
                                    )}>
                                      {availToSell.total}
                                      <Info className="h-3 w-3 text-muted-foreground" />
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="left" className="max-w-[260px]">
                                    <p className="font-medium mb-1">Composite products supported:</p>
                                    {availToSell.details.map((d, i) => (
                                      <p key={i} className="text-xs text-muted-foreground">
                                        {d.name}: {d.canMake} units
                                      </p>
                                    ))}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : (
                              <span className="text-xs text-muted-foreground">--</span>
                            )}
                          </td>
                        )}
                        <td className="p-3 text-right">
                          {rawItem.cost_per_unit ? (
                            <span className="text-xs tabular-nums text-muted-foreground">
                              {formatCurrency(rawItem.cost_per_unit)}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">--</span>
                          )}
                        </td>
                        <td className="p-3">
                          <Badge variant={rawStatus.variant}>{rawStatus.label}</Badge>
                        </td>
                        <td className="p-3 text-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleRawActive(rawItem);
                            }}
                            disabled={togglingActiveId === rawItem.id}
                            className={cn(
                              "transition-colors",
                              togglingActiveId === rawItem.id
                                ? "opacity-50 cursor-not-allowed"
                                : "hover:opacity-80 cursor-pointer"
                            )}
                            title={rawItem.is_active ? 'Deactivate item' : 'Activate item'}
                          >
                            {togglingActiveId === rawItem.id ? (
                              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                            ) : rawItem.is_active ? (
                              <ToggleRight className="h-5 w-5 text-green-600" />
                            ) : (
                              <ToggleLeft className="h-5 w-5 text-muted-foreground" />
                            )}
                          </button>
                        </td>
                        <td className="p-3 text-left">
                          <span className="text-xs text-muted-foreground">
                            {new Date(rawItem.created_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: '2-digit'
                            })}
                          </span>
                        </td>
                      </tr>

                      {/* Expanded panel: Associated composite products */}
                      {isRawExpanded && (
                        <tr className="border-b bg-muted/10">
                          <td colSpan={viewingAllLocations ? 11 : 11} className="p-0">
                            <div className="p-4" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                  Used in Composite Products
                                </h4>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="gap-1.5 text-xs"
                                  onClick={() => navigate(`/stock-control/raw-items?item=${rawItem.id}`)}
                                >
                                  View Details
                                </Button>
                              </div>
                              {isRawAssocLoading ? (
                                <div className="flex items-center justify-center py-6">
                                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                                </div>
                              ) : rawAssoc.length === 0 ? (
                                <p className="text-sm text-muted-foreground py-4 text-center">
                                  Not used as a component in any composite products.
                                </p>
                              ) : (
                                <table className="text-sm w-full">
                                  <thead>
                                    <tr className="border-b text-xs text-muted-foreground">
                                      <th className="text-left px-3 py-2 font-medium">Product</th>
                                      <th className="text-left px-3 py-2 font-medium">Type</th>
                                      <th className="text-right px-3 py-2 font-medium">Qty per Unit</th>
                                      <th className="text-right px-3 py-2 font-medium">Can Make</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {rawAssoc.map((ap) => {
                                      const canMake = ap.qtyPerUnit > 0 ? Math.floor(qty / ap.qtyPerUnit) : 0;
                                      return (
                                        <tr key={ap.parentId} className="border-b last:border-0 hover:bg-muted/30">
                                          <td className="px-3 py-2">
                                            <div className="flex items-center gap-2">
                                              <div className="w-7 h-7 rounded bg-muted flex items-center justify-center shrink-0 overflow-hidden">
                                                {ap.parentImage ? (
                                                  <img src={ap.parentImage} alt="" className="w-full h-full object-cover" />
                                                ) : (
                                                  <Package className="h-3.5 w-3.5 text-muted-foreground" />
                                                )}
                                              </div>
                                              <span className="font-medium">{ap.parentName}</span>
                                            </div>
                                          </td>
                                          <td className="px-3 py-2">
                                            <Badge variant="outline" className="text-[10px] capitalize">
                                              {ap.parentType?.replace('_', ' ') || 'composite'}
                                            </Badge>
                                          </td>
                                          <td className="px-3 py-2 text-right tabular-nums font-medium">
                                            {ap.qtyPerUnit}
                                          </td>
                                          <td className="px-3 py-2 text-right">
                                            <span className={cn(
                                              "tabular-nums font-semibold",
                                              canMake <= 0 && "text-red-600",
                                              canMake > 0 && canMake <= 5 && "text-yellow-600",
                                              canMake > 5 && "text-green-600",
                                            )}>
                                              {canMake}
                                            </span>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  }
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Track Item Dialog (Tabbed: Track Product / Add Raw Material) */}
      <ResponsiveModal open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <ResponsiveModalContent maxWidth="md">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Track Item</ResponsiveModalTitle>
            <ResponsiveModalDescription>
              Track an existing product or add a new raw material
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalBody>
            {/* Tab Switcher */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              <button
                type="button"
                onClick={() => setAddDialogTab('track')}
                className={cn(
                  'p-3 rounded-lg border-2 text-left transition-all',
                  addDialogTab === 'track'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-muted-foreground/50'
                )}
              >
                <p className="font-medium text-sm">Track Product</p>
                <p className="text-xs text-muted-foreground">
                  Enable inventory for existing product
                </p>
              </button>
              <button
                type="button"
                onClick={() => setAddDialogTab('raw_material')}
                className={cn(
                  'p-3 rounded-lg border-2 text-left transition-all',
                  addDialogTab === 'raw_material'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-muted-foreground/50'
                )}
              >
                <p className="font-medium text-sm">Add Raw Material</p>
                <p className="text-xs text-muted-foreground">
                  Create a new ingredient or supply
                </p>
              </button>
            </div>

            {addDialogTab === 'track' ? (
              /* Track Product Tab */
              <div className="grid gap-4">
                {productsWithoutInventory.length > 0 ? (
                  <>
                    <div className="space-y-2">
                      <Label>Select Product</Label>
                      <Select value={selectedProductId} onValueChange={setSelectedProductId}>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose a product" />
                        </SelectTrigger>
                        <SelectContent>
                          {productsWithoutInventory.map((product) => (
                            <SelectItem key={product.id} value={product.id}>
                              {product.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="initialQty">Initial Quantity</Label>
                      <Input
                        id="initialQty"
                        type="number"
                        min="0"
                        value={initialQuantity}
                        onChange={(e) => setInitialQuantity(e.target.value)}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="reorderPt">Reorder Point</Label>
                        <Input
                          id="reorderPt"
                          type="number"
                          min="0"
                          value={reorderPoint}
                          onChange={(e) => setReorderPoint(e.target.value)}
                          placeholder="Optional"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="reorderQty">Reorder Quantity</Label>
                        <Input
                          id="reorderQty"
                          type="number"
                          min="0"
                          value={reorderQuantity}
                          onChange={(e) => setReorderQuantity(e.target.value)}
                          placeholder="Optional"
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-6">
                    <Package className="h-10 w-10 text-muted-foreground/50 mx-auto mb-3" />
                    <p className="text-sm font-medium">All products are already tracked</p>
                    <p className="text-xs text-muted-foreground mt-1 mb-4">
                      Create a new product or add a raw material instead
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setAddDialogOpen(false);
                        navigate('/stock-control/manage?create=true');
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Create New Product
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              /* Add Raw Material Tab */
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="rawName">Name *</Label>
                  <Input
                    id="rawName"
                    value={rawMaterialName}
                    onChange={(e) => setRawMaterialName(e.target.value)}
                    placeholder="e.g., Ground Beef, Paper Cups"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="rawSku">SKU</Label>
                    <Input
                      id="rawSku"
                      value={rawMaterialSku}
                      onChange={(e) => setRawMaterialSku(e.target.value)}
                      placeholder="Optional"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Unit of Measure</Label>
                    <Select value={rawMaterialUnit} onValueChange={setRawMaterialUnit}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {rawUnitOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select value={rawMaterialCategory} onValueChange={setRawMaterialCategory}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {rawCategoryOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rawCost">Cost per Unit</Label>
                    <Input
                      id="rawCost"
                      type="number"
                      min="0"
                      step="0.01"
                      value={rawMaterialCost}
                      onChange={(e) => setRawMaterialCost(e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="rawInitialStock">Initial Stock</Label>
                  <Input
                    id="rawInitialStock"
                    type="number"
                    min="0"
                    value={rawMaterialInitialStock}
                    onChange={(e) => setRawMaterialInitialStock(e.target.value)}
                  />
                </div>
              </div>
            )}
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
            {addDialogTab === 'track' ? (
              <Button
                variant="blaze"
                onClick={handleAddInventory}
                disabled={!selectedProductId || adding || productsWithoutInventory.length === 0}
              >
                {adding && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Enable Tracking
              </Button>
            ) : (
              <Button
                variant="blaze"
                onClick={handleAddRawMaterial}
                disabled={!rawMaterialName.trim() || adding}
              >
                {adding && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Add Raw Material
              </Button>
            )}
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Adjustment Dialog */}
      <ResponsiveModal open={adjustDialogOpen} onOpenChange={setAdjustDialogOpen}>
        <ResponsiveModalContent maxWidth="sm">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Adjust Stock</ResponsiveModalTitle>
            <ResponsiveModalDescription>
              {selectedItem?.product?.name}
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalBody>
            <div className="grid gap-4">
              {/* Current stock display */}
              <div className="p-3 rounded-lg bg-muted/50 border">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground text-xs">Total</span>
                    <p className="font-semibold tabular-nums">{selectedItem?.quantity_on_hand?.toLocaleString() ?? 0}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground text-xs">BOH (Storage)</span>
                    <p className="font-semibold tabular-nums text-blue-600">{(selectedItem?.quantity_boh ?? 0).toLocaleString()}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground text-xs">FOH (Selling)</span>
                    <p className="font-semibold tabular-nums text-green-600">{(selectedItem?.quantity_foh ?? selectedItem?.quantity_on_hand ?? 0).toLocaleString()}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Adjustment Type</Label>
                <div className="grid grid-cols-2 gap-2">
                  {(Object.entries(adjustmentTypeLabels) as [AdjustmentType, typeof adjustmentTypeLabels.received][])
                    .filter(([type]) => type !== 'received_boh')
                    .map(([type, config]) => (
                      <Button
                        key={type}
                        variant={adjustmentType === type ? 'secondary' : 'outline'}
                        size="sm"
                        onClick={() => setAdjustmentType(type as AdjustmentType)}
                        className="justify-start gap-2"
                      >
                        <config.icon className={cn('h-4 w-4', config.color)} />
                        {type === 'received' ? 'Receive Stock' : config.label}
                      </Button>
                    )
                  )}
                </div>
              </div>

              {/* BOH/FOH target selector for receiving stock */}
              {adjustmentType === 'received' && (
                <div className="space-y-2">
                  <Label>Receive Into</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setAdjustmentTarget('foh')}
                      className={cn(
                        'p-3 rounded-lg border-2 text-left transition-all',
                        adjustmentTarget === 'foh'
                          ? 'border-green-500 bg-green-500/5'
                          : 'border-border hover:border-muted-foreground/50'
                      )}
                    >
                      <p className="text-sm font-medium">FOH (Selling Floor)</p>
                      <p className="text-xs text-muted-foreground">Available to sell immediately</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setAdjustmentTarget('boh')}
                      className={cn(
                        'p-3 rounded-lg border-2 text-left transition-all',
                        adjustmentTarget === 'boh'
                          ? 'border-blue-500 bg-blue-500/5'
                          : 'border-border hover:border-muted-foreground/50'
                      )}
                    >
                      <p className="text-sm font-medium">BOH (Storage)</p>
                      <p className="text-xs text-muted-foreground">Back of house / warehouse</p>
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="adjustQty">
                  Quantity {['waste'].includes(adjustmentType) ? 'to Remove' : 'to Add'}
                </Label>
                <Input
                  id="adjustQty"
                  type="number"
                  min="0"
                  step="1"
                  value={adjustmentQuantity}
                  onChange={(e) => setAdjustmentQuantity(e.target.value)}
                  placeholder="Enter quantity"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="adjustNotes">Notes (Optional)</Label>
                <Textarea
                  id="adjustNotes"
                  value={adjustmentNotes}
                  onChange={(e) => setAdjustmentNotes(e.target.value)}
                  placeholder="Reason for adjustment..."
                  rows={2}
                />
              </div>
            </div>
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setAdjustDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="blaze"
              onClick={handleAdjustment}
              disabled={!adjustmentQuantity || adjusting}
            >
              {adjusting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Apply Adjustment
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Stock Count Dialog */}
      <ResponsiveModal open={countDialogOpen} onOpenChange={setCountDialogOpen}>
        <ResponsiveModalContent maxWidth="md">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Stock Count</ResponsiveModalTitle>
            <ResponsiveModalDescription>
              {countItem?.product?.name}
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalBody>
            <div className="grid gap-4">
              {/* Current Stock Display */}
              <div className="p-3 rounded-lg bg-muted/50 border">
                <p className="text-sm font-medium mb-2">Current System Stock</p>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Total:</span>{' '}
                    <span className="font-semibold tabular-nums">{countItem?.quantity_on_hand?.toLocaleString() ?? 0}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">BOH:</span>{' '}
                    <span className="font-semibold tabular-nums text-blue-600">{(countItem?.quantity_boh ?? 0).toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">FOH:</span>{' '}
                    <span className="font-semibold tabular-nums text-green-600">{(countItem?.quantity_foh ?? countItem?.quantity_on_hand ?? 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Count Mode Toggle */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Count Mode</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setCountMode('total')}
                    className={cn(
                      'p-3 rounded-lg border-2 text-left transition-all',
                      countMode === 'total'
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-muted-foreground/50'
                    )}
                  >
                    <p className="font-medium text-sm">Total Only</p>
                    <p className="text-xs text-muted-foreground">
                      Update the total quantity
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCountMode('split');
                      // When switching to split mode, if BOH is 0, put current total in FOH
                      // to allow user to redistribute
                      if ((parseFloat(countBOH) || 0) === 0 && (parseFloat(countFOH) || 0) === 0) {
                        const currentTotal = countItem?.quantity_on_hand ?? 0;
                        setCountBOH('0');
                        setCountFOH(currentTotal.toString());
                      }
                    }}
                    className={cn(
                      'p-3 rounded-lg border-2 text-left transition-all',
                      countMode === 'split'
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-muted-foreground/50'
                    )}
                  >
                    <p className="font-medium text-sm">BOH / FOH Split</p>
                    <p className="text-xs text-muted-foreground">
                      Count storage and floor separately
                    </p>
                  </button>
                </div>
              </div>

              {countMode === 'total' ? (
                /* Total Count Mode */
                <div className="space-y-2">
                  <Label htmlFor="countQty">Actual Quantity Counted</Label>
                  <Input
                    id="countQty"
                    type="number"
                    min="0"
                    step="1"
                    value={countQuantity}
                    onChange={(e) => setCountQuantity(e.target.value)}
                    placeholder="Enter actual count"
                    className="text-lg font-semibold"
                  />
                  {countQuantity !== '' && countItem && parseFloat(countQuantity) !== countItem.quantity_on_hand && (
                    <p className="text-sm text-muted-foreground">
                      Difference:{' '}
                      <span
                        className={cn(
                          'font-medium',
                          parseFloat(countQuantity) > countItem.quantity_on_hand
                            ? 'text-green-600'
                            : 'text-red-600'
                        )}
                      >
                        {parseFloat(countQuantity) > countItem.quantity_on_hand ? '+' : ''}
                        {(parseFloat(countQuantity) - countItem.quantity_on_hand).toLocaleString()}
                      </span>
                    </p>
                  )}
                </div>
              ) : (
                /* BOH/FOH Split Mode */
                <div className="space-y-4">
                  {/* Helper message when converting from total-only */}
                  {countItem && (countItem.quantity_boh ?? 0) === 0 && countItem.quantity_on_hand > 0 && (
                    <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                      <p className="text-sm text-blue-800 dark:text-blue-200">
                        <span className="font-medium">Converting to BOH/FOH tracking:</span> Your current total of{' '}
                        <span className="font-semibold">{countItem.quantity_on_hand.toLocaleString()}</span> units is shown in FOH.
                        Adjust the values below to split between storage (BOH) and sales floor (FOH).
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="countBOH" className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-blue-500" />
                        Back of House (Storage)
                      </Label>
                      <Input
                        id="countBOH"
                        type="number"
                        min="0"
                        step="1"
                        value={countBOH}
                        onChange={(e) => setCountBOH(e.target.value)}
                        placeholder="0"
                        className="text-lg font-semibold"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="countFOH" className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-green-500" />
                        Front of House (Floor)
                      </Label>
                      <Input
                        id="countFOH"
                        type="number"
                        min="0"
                        step="1"
                        value={countFOH}
                        onChange={(e) => setCountFOH(e.target.value)}
                        placeholder="0"
                        className="text-lg font-semibold"
                      />
                    </div>
                  </div>

                  {/* Calculated Total */}
                  <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">New Total</span>
                      <span className="text-xl font-bold tabular-nums">
                        {((parseFloat(countBOH) || 0) + (parseFloat(countFOH) || 0)).toLocaleString()}
                      </span>
                    </div>
                    {countItem && (
                      (() => {
                        const newTotal = (parseFloat(countBOH) || 0) + (parseFloat(countFOH) || 0);
                        const diff = newTotal - countItem.quantity_on_hand;
                        if (diff !== 0) {
                          return (
                            <p className="text-sm text-muted-foreground mt-1">
                              Difference:{' '}
                              <span
                                className={cn(
                                  'font-medium',
                                  diff > 0 ? 'text-green-600' : 'text-red-600'
                                )}
                              >
                                {diff > 0 ? '+' : ''}{diff.toLocaleString()}
                              </span>
                            </p>
                          );
                        }
                        return null;
                      })()
                    )}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="countNotes">Notes (Optional)</Label>
                <Textarea
                  id="countNotes"
                  value={countNotes}
                  onChange={(e) => setCountNotes(e.target.value)}
                  placeholder="Notes about this count..."
                  rows={2}
                />
              </div>
            </div>
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setCountDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="blaze"
              onClick={handleStockCount}
              disabled={(countMode === 'total' && countQuantity === '') || counting}
            >
              {counting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Save Count
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* History Dialog */}
      <ResponsiveModal open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <ResponsiveModalContent maxWidth="lg">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Transaction History</ResponsiveModalTitle>
            <ResponsiveModalDescription>
              {historyItem?.product?.name}
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalBody>
            {loadingHistory ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No transactions recorded yet
              </div>
            ) : (
              <div className="space-y-2">
                {transactions.map((txn) => (
                  <div
                    key={txn.id}
                    className="flex items-start gap-3 p-3 rounded-lg border"
                  >
                    <div
                      className={cn(
                        'mt-0.5 p-1.5 rounded-full',
                        txn.quantity_change > 0
                          ? 'bg-green-100 dark:bg-green-900/30'
                          : 'bg-red-100 dark:bg-red-900/30'
                      )}
                    >
                      {txn.quantity_change > 0 ? (
                        <TrendingUp className="h-3 w-3 text-green-600" />
                      ) : (
                        <TrendingDown className="h-3 w-3 text-red-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge
                          variant="outline"
                          className={cn('text-xs', transactionTypeColors[txn.transaction_type])}
                        >
                          {txn.transaction_type}
                        </Badge>
                        <span
                          className={cn(
                            'text-sm font-semibold tabular-nums',
                            txn.quantity_change > 0 ? 'text-green-600' : 'text-red-600'
                          )}
                        >
                          {txn.quantity_change > 0 ? '+' : ''}
                          {txn.quantity_change.toLocaleString()}
                        </span>
                      </div>
                      {txn.notes && (
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {txn.notes}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                        <span>
                          {new Date(txn.created_at).toLocaleString()}
                        </span>
                        {txn.user && (
                          <>
                            <span>•</span>
                            <span>
                              {txn.user.first_name} {txn.user.last_name}
                            </span>
                          </>
                        )}
                        <span>•</span>
                        <span className="tabular-nums">
                          {txn.quantity_before?.toLocaleString()} → {txn.quantity_after?.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setHistoryDialogOpen(false)}>
              Close
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* BOH/FOH Transfer Dialog */}
      <BOHTransferDialog
        open={transferDialogOpen}
        onOpenChange={setTransferDialogOpen}
        inventoryItem={transferItem}
        onTransfer={handleTransfer}
      />

      {/* Delete / Deactivate Dialog */}
      <ResponsiveModal open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <ResponsiveModalContent maxWidth="sm">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>
              {permanentDelete
                ? 'Permanently Delete'
                : getDeleteTargetStock() > 0
                  ? 'Zero Out and Deactivate'
                  : 'Deactivate Item'}
            </ResponsiveModalTitle>
            <ResponsiveModalDescription>
              {getDeleteTargetName()}
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalBody>
            <div className="space-y-4">
              {/* Mode selector */}
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setPermanentDelete(false)}
                  className={cn(
                    'p-3 rounded-lg border-2 text-left transition-all',
                    !permanentDelete
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-muted-foreground/50'
                  )}
                >
                  <p className="font-medium text-sm">Deactivate</p>
                  <p className="text-xs text-muted-foreground">
                    Hide from POS. Can reactivate later.
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setPermanentDelete(true)}
                  className={cn(
                    'p-3 rounded-lg border-2 text-left transition-all',
                    permanentDelete
                      ? 'border-red-500 bg-red-500/5'
                      : 'border-border hover:border-muted-foreground/50'
                  )}
                >
                  <p className="font-medium text-sm text-red-600">Permanently Delete</p>
                  <p className="text-xs text-muted-foreground">
                    Remove forever. Cannot be undone.
                  </p>
                </button>
              </div>

              {permanentDelete ? (
                <div className="space-y-3">
                  <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-red-800 dark:text-red-200">
                          This action is irreversible
                        </p>
                        <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                          The product, all inventory records, and transaction history will be permanently removed.
                          Order history will be preserved but the product reference will be cleared.
                          A deletion audit log will be created.
                        </p>
                      </div>
                    </div>
                  </div>
                  {getDeleteTargetStock() > 0 && (
                    <div className="p-2 rounded bg-muted/50 border">
                      <p className="text-xs text-muted-foreground">
                        Current stock: <span className="font-medium">{getDeleteTargetStock().toLocaleString()}</span> units will be removed without waste logging.
                      </p>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="deleteNotes">Reason for deletion (optional)</Label>
                    <Textarea
                      id="deleteNotes"
                      value={deleteNotes}
                      onChange={(e) => setDeleteNotes(e.target.value)}
                      placeholder="Why is this product being permanently removed..."
                      rows={2}
                    />
                  </div>
                </div>
              ) : getDeleteTargetStock() > 0 ? (
                <div className="space-y-3">
                  <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800">
                    <p className="text-sm font-medium text-red-800 dark:text-red-200">
                      This item has {getDeleteTargetStock().toLocaleString()} units in stock
                    </p>
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                      All remaining stock will be recorded as a loss (waste) before deactivating.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="deleteNotes">Reason (optional)</Label>
                    <Textarea
                      id="deleteNotes"
                      value={deleteNotes}
                      onChange={(e) => setDeleteNotes(e.target.value)}
                      placeholder="Reason for deactivating and writing off stock..."
                      rows={2}
                    />
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  This will deactivate the item and hide it from the inventory overview.
                  You can reactivate it later from the Inactive filter.
                </p>
              )}
            </div>
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            {permanentDelete ? (
              <Button
                variant="destructive"
                onClick={handlePermanentDelete}
                disabled={deleting}
              >
                {deleting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Permanently Delete
              </Button>
            ) : (
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {getDeleteTargetStock() > 0 ? 'Zero Out and Deactivate' : 'Deactivate'}
              </Button>
            )}
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      <ProductImportDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
      />

      {posImportSource && (
        <POSImportDialog
          open={!!posImportSource}
          onOpenChange={(open) => {
            if (!open) setPosImportSource(null);
          }}
          sourceId={posImportSource}
        />
      )}
    </div>
  );
}
